
import { Chart } from "chart.js";
import { mapState, mapStores } from "pinia";
import { defineComponent, PropType, shallowRef } from "vue";
import { Widget, WidgetType } from "~/models/widget.model";
import WidgetBase, { WidgetMode } from "./widget.vue";
import { Measure } from "~/api/models/measure.model";
import { levelColor } from "~/variables";
import moment from "moment";
import { AuraIndicator } from "~/configs/indicators/aura-indicators.config";
import { GridStack } from "gridstack";
import { useDashboardStore } from "~/store/dashboard.store";
import WidgetContentBase from '~/components/widgets/widget-content-base';
import { fake } from '~/components/widgets/widget-histogram-fake-data';

export default defineComponent({

    extends: WidgetContentBase,

    data() {
        return {
            chart: null,
            suggestedMin: {
                [AuraIndicator.CO2]: 400,
                [AuraIndicator.humidity]: 10,
                [AuraIndicator.pressure]: 950,
                [AuraIndicator.temperature]: 15,
                
            },
            suggestedMax: {
                [AuraIndicator.CO2]: 1000,
                [AuraIndicator.humidity]: 90,
                [AuraIndicator.pressure]: 1050,
                [AuraIndicator.temperature]: 45,
            }
        };
    },

    computed: {


        ...mapStores(useDashboardStore),
        ...mapState(useDashboardStore, ['currentStation', 'averages','indicator', 'mode', 'since', 'before', 'step']),

        data(): Measure[] {
            return this.fake ? fake : this.averages;
        },

        dateFormat(): string {
            switch(this.mode) {
                case 'hour':
                    return 'LT';
                case 'day':
                case 'week':
                case 'month':
                    return 'lll';
                default: 
                    return 'LT';
            }
        },

        min(): string {
            const fakeDates = this.data?.map(m => m.date).sort((a,b) => a-b);
            return this.fake ? moment.unix(fakeDates[0]).toISOString() : this.since.clone().subtract(this.step / 2, 's').toISOString()
        },

        max(): string {
            const fakeDates = this.data?.map(m => m.date).sort((a,b) => b-a);
            return this.fake ? moment.unix(fakeDates[0]).toISOString() : this.before.clone().subtract(this.step / 2, 's').toISOString();
        },

        chartData(): {x: Date, y: number}[] {
            if(!this.data) {
                return [];
            }
            return this.data.map((measure: Measure) => ({x: moment.unix(measure.date).toDate(), y: Math.round(measure.values[this.indicator]) }))
        },

        aqis(): number[] {
            if(!this.data) {
                return [];
            }
            return this.data.map((measure: Measure) => {
                const key: string =`${this.indicator}_aqi`;
                const aqi: number | undefined = key in measure.values ? measure.values[`${this.indicator}_aqi`]: -1;
                return Math.floor(aqi / 25);
            });
        },

        barColors(): string[] {
            return this.aqis.map(aqi => aqi >= 0 ? levelColor[aqi]: '#66BA7E');
        },

        title(): string {
            switch(this.indicator) {
                case 'CO2':
                    return this.$t('dashboard.histogram_widget.co2_title');
                case 'temperature':
                    return this.$t('dashboard.histogram_widget.temperature_title');
                case 'humidity':
                    return this.$t('dashboard.histogram_widget.humidity_title');
                case 'pressure':
                    return this.$t('dashboard.histogram_widget.pressure_title');
                default:
                    return '';
            }
        },
    },

    mounted(): void {
        this.init();
    },

    methods: {
        init() {
            const chartElement: HTMLCanvasElement = this.$refs?.chart as HTMLCanvasElement;

            if(!chartElement) {
                return;
            }

            const datasets = [];

            if(this.indicator) {
                datasets.push({
                    label: this.indicator,
                    backgroundColor: this.barColors,
                    data: this.chartData
                })
            }

            this.chart = shallowRef(new Chart(chartElement.getContext('2d'), {
                type: 'bar',
                data: {
                    datasets
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                        y: {
                            suggestedMin: this.suggestedMin[this.indicator],
                            suggestedMax: this.suggestedMax[this.indicator],
                        },
                        x: {
                            offset: false,
                            // min: this.min,
                            // max: this.max,
                            type: 'time',
                            time: {
                                unit: 'minute',
                                displayFormats: {
                                    minute: 'HH:mm',
                                    hour: 'HH:mm',
                                    day: 'll',

                                }
                            },
                            grid: {
                                display: false,
                            },
                            ticks: {
                                maxRotation: 0,
                                maxTicksLimit: 10,
                            }
                        }
                    },

                }
            }));
        },


        updateChart() {
            if(!this.chart) {
                return;
            }
            try {
                this.chart.data = {
                    datasets: [{
                        label: this.indicator,
                        backgroundColor: this.barColors,
                        data: this.chartData,
                        borderRadius: 5,
                        borderSkipped: false,
                    }]
                };
                this.chart.options.scales.x.min = this.min;
                this.chart.options.scales.x.max = this.max;
                this.chart.options.scales.y.suggestedMin = this.suggestedMin[this.indicator];
                this.chart.options.scales.y.suggestedMax = this.suggestedMax[this.indicator];

                this.chart.options.scales.x.time.unit = this.mode === 'week' || this.mode === 'month' ? 'day': this.mode === 'day' ? 'hour': 'minute';
                this.chart.update();
            } catch(e) {
                console.error(e);
                this.$notifyError(`An error occured while updating chart ${this.indicator || ''} and options ${this.chart.options}`,e);
            }
        }
    },

    watch: {
        indicator() {
            this.updateChart();
        },

        async averages() {
            await this.updateChart();
        },
    }
})
